import { Fragment, HTMLAttributes } from 'react';

import { useTranslation } from 'react-i18next';

import { useFilterQuery } from '@/hooks/use-filter-query';
import { useFilterValues } from '@/hooks/use-filter-values';
import { cn } from '@/lib/utils';
import { FilterConfig } from '@/types/filters';

import { Button } from '../../ui/button';
import {
  DataTableSwitchFilter,
  FilterAutocomplete,
  FilterDateRange,
  FilterDateRangePickerV2,
  FilterDynamicAutocomplete,
  FilterDynamicAutocompletePaginated,
  FilterRangeMode,
  FilterSearch,
  PresetFiltersView,
} from './index';

interface IDataTableFilterProps extends HTMLAttributes<HTMLDivElement> {
  filtersConfig: FilterConfig[];
  isClearButtonVisible?: boolean;
  onFilterValueChange?: (key: string, value: string | string[]) => void | boolean;
  onSearchFocus?: (event: React.FocusEvent<HTMLInputElement>) => boolean | void;
}

export function DataTableFilters({
  filtersConfig,
  isClearButtonVisible = true,
  ...props
}: IDataTableFilterProps) {
  const { t } = useTranslation();
  const { get, set, clearAll } = useFilterQuery();
  const { values, isEmpty } = useFilterValues(filtersConfig);

  const activeFilterKeys = Object.keys(values); // only filters with values

  const renderFilterItem = (config: FilterConfig) => {
    const setFilterValue = (key: string, value: string | string[]): void | boolean => {
      // Call onFilterValueChange first if provided
      // If it returns false, prevent the default filter change
      if (props.onFilterValueChange) {
        const result = props.onFilterValueChange(key, value);
        if (result === false) {
          return false; // Return false to signal change was prevented
        }
      }
      set(key, value);
      return undefined; // Return undefined for successful change (or no handler)
    };
    const currentValue = get(config.name);

    switch (config.type) {
      case 'search':
        return (
          <FilterSearch
            config={config}
            value={currentValue[0]}
            onChange={setFilterValue}
            onFocus={props.onSearchFocus}
          />
        );
      case 'autocomplete':
        return (
          <FilterAutocomplete config={config} value={currentValue} onChange={setFilterValue} />
        );
      case 'dynamic-autocomplete':
        return (
          <FilterDynamicAutocomplete
            config={config}
            value={currentValue}
            onChange={setFilterValue}
            appliedFilters={values}
          />
        );
      case 'dynamic-autocomplete-paginated':
        return (
          <FilterDynamicAutocompletePaginated
            config={config}
            value={currentValue}
            onChange={setFilterValue}
          />
        );
      case 'rangeMode':
        return (
          <FilterRangeMode config={config} value={currentValue[0]} onChange={setFilterValue} />
        );
      case 'presetFilters':
        return (
          <PresetFiltersView
            config={config}
            value={currentValue[0] || ''}
            onChange={setFilterValue}
          />
        );
      case 'dateRange':
        return (
          <FilterDateRange config={config} value={currentValue[0]} onChange={setFilterValue} />
        );
      case 'dateRangePickerV2':
        return (
          <FilterDateRangePickerV2
            config={config}
            value={currentValue[0]}
            onChange={setFilterValue}
          />
        );
      case 'switch':
        return (
          <DataTableSwitchFilter
            config={config}
            value={currentValue[0] === 'true'}
            onChange={setFilterValue}
          />
        );
      // Extend others if needed...

      default:
        return null;
    }
  };

  return (
    <div className={cn('flex items-center gap-2 flex-wrap p-1', props.className)}>
      {filtersConfig.map(config => (
        <Fragment key={config.name}>{renderFilterItem(config)}</Fragment>
      ))}
      {isClearButtonVisible && !isEmpty && (
        <Button variant="ghost" onClick={() => clearAll(activeFilterKeys)} size="sm">
          {t('common:filters.clear')}
        </Button>
      )}
    </div>
  );
}
